import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import BibleStudyBulkUpload from './BibleStudyBulkUpload';

const PAGE_SIZE = 50;

/** type="month" "YYYY-MM" → study_date(YYYYMMDD) 해당 월 범위 */
function monthInputToStudyDateRange(monthStr) {
  if (!monthStr || typeof monthStr !== 'string') return null;
  const parts = monthStr.split('-');
  if (parts.length !== 2) return null;
  const y = Number(parts[0], 10);
  const m = Number(parts[1], 10);
  if (!y || !m || m < 1 || m > 12) return null;
  const ym = `${String(y)}${String(m).padStart(2, '0')}`;
  const lastDay = new Date(y, m, 0).getDate();
  return {
    from: `${ym}01`,
    to: `${ym}${String(lastDay).padStart(2, '0')}`,
  };
}

function sanitizeTextSearch(raw) {
  return raw.trim().replace(/[%_,]/g, '');
}

function getCurrentMonthInput() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const BibleStudyList = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  /** 선택 삭제용 행 id (활성 Y/N 체크와 별개) */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const observerTarget = useRef(null);

  const [searchMonth, setSearchMonth] = useState(() => getCurrentMonthInput());
  const [searchText, setSearchText] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [currentFilters, setCurrentFilters] = useState({
    monthRaw: '',
    textRaw: '',
    langRaw: ''
  });

  const buildQuery = (monthRaw, textRaw, langRaw) => {
    let query = supabase.from('bible_study').select('*', { count: 'exact' });

    const range = monthInputToStudyDateRange(monthRaw);
    if (range) {
      query = query.gte('study_date', range.from).lte('study_date', range.to);
    }

    const lang = typeof langRaw === 'string' ? langRaw.trim() : '';
    if (lang) {
      query = query.eq('language', lang);
    }

    const text = sanitizeTextSearch(textRaw);
    if (text) {
      const p = `%${text}%`;
      query = query.or(
        `title.ilike.${p},subtitle.ilike.${p},hymn.ilike.${p},grace_sharing_text.ilike.${p},scripture_reference.ilike.${p},scripture_text.ilike.${p},study_date.ilike.${p}`
      );
    }

    return query.order('study_date', { ascending: false });
  };

  const runFetch = async (monthRaw, textRaw, langRaw) => {
    setLoading(true);
    setError(null);
    setOffset(0);
    setHasMore(true);
    setStudies([]);
    setSelectedIds(new Set());
    setCurrentFilters({ monthRaw, textRaw, langRaw });

    try {
      const query = buildQuery(monthRaw, textRaw, langRaw);
      const { data, error: fetchError } = await query.range(0, PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      setStudies(data || []);
      setOffset(PAGE_SIZE);
      setHasMore((data?.length || 0) >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError(e.message || '목록 조회에 실패했습니다.');
      setStudies([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const query = buildQuery(currentFilters.monthRaw, currentFilters.textRaw, currentFilters.langRaw);
      const { data, error: fetchError } = await query.range(offset, offset + PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      const newData = data || [];
      setStudies(prev => [...prev, ...newData]);
      setOffset(prev => prev + PAGE_SIZE);
      setHasMore(newData.length >= PAGE_SIZE);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    runFetch(searchMonth, '', '');
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadingMore, loading, offset, currentFilters]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    runFetch(searchMonth, searchText, languageFilter);
  };

  const handleResetSearch = () => {
    setSearchMonth('');
    setSearchText('');
    setLanguageFilter('');
    runFetch('', '', '');
  };

  const handleRowClick = (id) => {
    navigate(`/bible-study/${id}`);
  };

  const handleCreate = () => {
    navigate('/bible-study/new');
  };

  const handleActiveChange = async (event, study) => {
    event.stopPropagation();
    const newIsActive = study.is_active === 'Y' ? 'N' : 'Y';
    const originalStudies = [...studies];

    setStudies((prevStudies) =>
      prevStudies.map((s) => (s.id === study.id ? { ...s, is_active: newIsActive } : s))
    );

    const { error: updError } = await supabase
      .from('bible_study')
      .update({ is_active: newIsActive })
      .eq('id', study.id);

    if (updError) {
      setError(`활성 상태 변경 실패: ${updError.message}`);
      setStudies(originalStudies);
    }
  };

  const handleBulkUploadSuccess = () => {
    runFetch(searchMonth, searchText, languageFilter);
  };

  const toggleRowSelected = (id, event) => {
    event.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const ids = studies.map((s) => s.id);
    if (!ids.length) return;
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleBulkDelete = async (event) => {
    event?.preventDefault?.();
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (
      !window.confirm(
        `선택한 ${ids.length}건을 삭제할까요?\n연결된 데이터가 있으면 삭제가 거부될 수 있습니다.\n이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      // .select('id') 로 실제 삭제된 행만 받음. RLS로 막히면 에러 없이 0건일 수 있음(그때 목록에서 지우면 안 됨).
      const { data: deletedRows, error: delError } = await supabase
        .from('bible_study')
        .delete()
        .in('id', ids)
        .select('id');

      if (delError) throw delError;

      const deletedCount = deletedRows?.length ?? 0;
      if (deletedCount === 0) {
        setError(
          '삭제된 행이 없습니다. 로그인 세션이 만료되었거나, DB RLS 정책(bible_study 삭제는 users.is_admin=true 인 사용자만 허용)에 걸렸을 수 있습니다. 다시 로그인하거나 Supabase에서 해당 계정의 users.is_admin 여부를 확인하세요.'
        );
        return;
      }

      const deletedIdSet = new Set((deletedRows || []).map((r) => r.id));
      setStudies((prev) => prev.filter((s) => !deletedIdSet.has(s.id)));
      setSelectedIds(new Set());

      if (deletedCount < ids.length) {
        setError(
          `일부만 삭제되었습니다 (${deletedCount}/${ids.length}건). 나머지는 권한(RLS) 또는 이미 삭제된 id일 수 있습니다.`
        );
      }
    } catch (e) {
      console.error(e);
      setError(e.message || '선택 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const allVisibleSelected =
    studies.length > 0 && studies.every((s) => selectedIds.has(s.id));

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">성경공부 목록</h6>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          {selectedIds.size > 0 && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={deleting}
              onClick={handleBulkDelete}
            >
              {deleting ? '삭제 중…' : `선택 삭제 (${selectedIds.size})`}
            </button>
          )}
          <button className="btn btn-success btn-sm" onClick={() => setShowBulkUpload(true)}>
            일괄 입력
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleCreate}>
            새 성경공부
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}

        <form className="mb-4 p-3 bg-light rounded border" onSubmit={handleSearchSubmit}>
          <div className="form-row align-items-end">
            <div className="form-group col-md-3 col-lg-2 mb-2 mb-md-0">
              <label htmlFor="bs-search-month" className="small font-weight-bold mb-1">
                검색 월
              </label>
              <input
                id="bs-search-month"
                type="month"
                className="form-control form-control-sm"
                value={searchMonth}
                onChange={(e) => setSearchMonth(e.target.value)}
              />
            </div>
            <div className="form-group col-md-3 col-lg-2 mb-2 mb-md-0">
              <label htmlFor="bs-language" className="small font-weight-bold mb-1">
                언어 (language)
              </label>
              <input
                id="bs-language"
                type="text"
                className="form-control form-control-sm"
                list="bs-language-list"
                placeholder="전체"
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
                autoComplete="off"
              />
              <datalist id="bs-language-list">
                <option value="ko" />
                <option value="en" />
                <option value="ja" />
                <option value="zh" />
              </datalist>
            </div>
            <div className="form-group col-md-6 col-lg-4 mb-2 mb-md-0">
              <label htmlFor="bs-text-search" className="small font-weight-bold mb-1">
                검색 단어
              </label>
              <input
                id="bs-text-search"
                type="text"
                className="form-control form-control-sm"
                placeholder="제목, 부제, 찬송, 말씀 본문 등"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="form-group col-md-12 col-lg-4 mb-0 d-flex flex-wrap align-items-end" style={{ gap: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                검색
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleResetSearch}
                disabled={loading}
              >
                조건 초기화
              </button>
            </div>
          </div>
          <small className="text-muted d-block mt-2 mb-0">
            검색 월은 <code>study_date</code>(YYYYMMDD)가 해당 월에 속하는 행만 표시합니다. 언어는{' '}
            <code>language</code>와 정확히 일치합니다. 검색 단어는 제목·부제·찬송·은혜 나눔·말씀 참조·본문·진행일에서 부분
            일치(대소문자 무시)합니다.
          </small>
        </form>

        {loading ? (
          <div className="text-muted py-3">조회 중...</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: 42 }} title="현재 목록에 보이는 행만 전체 선택">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={!studies.length || loading || deleting}
                        onChange={toggleSelectAllVisible}
                        aria-label="현재 목록 전체 선택"
                      />
                    </th>
                    <th>부제목</th>
                    <th>제목</th>
                    <th>언어</th>
                    <th>말씀 본문</th>
                    <th style={{ width: 56 }}>활성</th>
                  </tr>
                </thead>
                <tbody>
                  {studies.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        등록된 성경공부가 없습니다.
                      </td>
                    </tr>
                  )}
                  {studies.map((study) => (
                    <tr
                      key={study.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(study.id)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(study.id)}
                        onChange={(e) => toggleRowSelected(study.id, e)}
                        aria-label={`선택: ${study.title || study.id}`}
                      />
                    </td>                    
                    <td>{study.subtitle || '-'}</td>
                    <td>{study.title}</td>
                    <td>{study.language || '-'}</td>
                    <td>{study.scripture_reference}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={study.is_active === 'Y'}
                        onChange={(e) => handleActiveChange(e, study)}
                        title="활성 여부"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 스크롤 페이징 트리거 */}
          <div ref={observerTarget} className="text-center py-4">
            {loadingMore && (
              <div className="text-muted">
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                더 불러오는 중...
              </div>
            )}
            {!hasMore && studies.length > 0 && (
              <small className="text-muted">모든 데이터를 불러왔습니다.</small>
            )}
          </div>
          </>
        )}
      </div>
      {showBulkUpload && (
        <BibleStudyBulkUpload
          onClose={() => {
            setShowBulkUpload(false);
            runFetch(searchMonth, searchText, languageFilter);
          }}
          onSuccess={handleBulkUploadSuccess}
        />
      )}
    </div>
  );
};

export default BibleStudyList;
